import style from "@/styles/pages/Login/Login.module.css";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useLocation, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createApiClient } from "@/lib/Requests";
import { useEffect, useState } from "react";
import {
  setAlloyApplicationToken,
  setAmortizationLength,
  setApplicationCategory,
  setApplicationEndpointReached,
  setApplicationProgress,
  setDownPayment,
  setIdVerificationSubmitted,
  setIsCoApplicant,
  setJointAccepted,
  setJointCode,
  setJointPrimaryApplicant,
  setJointRelationship,
  setPurchaseTypeSelected,
  setSearchingForProperty,
  setTermLength,
  setTotalLoan,
  setUnderStaffReview,
  setWelcomeEmailSent,
} from "@/slices/applicationSlice";
import {
  clearApplicantState,
  clearJointState,
  setCitizenshipStatus,
  setCredentialsCreated,
  setDateOfBirthDay,
  setDateOfBirthMonth,
  setDateOfBirthYear,
  setFirstName,
  setFirstTimeBuyer,
  setLastName,
  setMaritalStatus,
  setMiddleName,
  setNumOfDependents,
  setPhoneNumber,
  setPhoneType,
  setRecentResident,
  setSin,
  setTitle,
  setIdVerificationStatus,
  setApplicantId,
  setAlloySessionId,
  setTwilioResult,
  setIdvId,
  setIsProspect,
  clearVerificationCounters,
  setEmailAddress,
  setExistingMemberFields,
  setFmApplicationId,
  setFmApplicantId,
  setCustNo
} from "@/slices/applicantSlice";
import {
  clearAddressState,
  setAddressId,
  setCity,
  setCountry,
  setPostalCode,
  setProvince,
  setStreetName,
  setStreetNumber,
  setStreetType,
  setUnitNumber,
  setUnitType
} from "@/slices/homeAddressSlice";
import {
  clearPropertyAddressState
} from "@/slices/propertyAddressSlice";
import { clearMailingAddressState } from "@/slices/mailingAddressSlice";
import {
  clearCredentialState,
  setAccessToken,
  setEmail,
} from "@/slices/credentialSlice";
import { clearProgressState, setLegalComplete } from "@/slices/progressSlice";
import { clearLegalTermsState, setAMLFieldsCompleted, setMeridianOnly, setNatureOfBanking, setOntarioWork, setOwnerOperator, setPolExposed, setPrimaryOtherPurposeGIC, setPrimaryPurpose, setPrimaryPurposeGIC, setResStatus, setTaxNotCanadaNotUS, setTaxResidentorCitizenUs } from "@/slices/legalTermsSlice";
import { RootState } from "@/store/dtm";
import { useForm } from "react-hook-form";
import { UserDto } from "@/lib/DTMWebAPIClient.generated";
import { applicationCategory } from "@/types/applicationType";
import {
  clearPropertyInfoState,
  setClosingDate,
  setFinancingConditionExists,
  setFinancingConditionDate,
  setFundingDate,
  setMortgageBalance,
  setNumOfRentalProperties,
  setNumOfRentalUnits,
  setOriginalPurchasePrice,
  setOwnOtherRentalProperties,
  setPropertyValue,
  setPurchasePrice,
  setPurposeOfProperty,
  setRentOut,
  setSeparateUnit,
} from "@/slices/propertyInfoSlice";
import {
  clearPreviousAddressState,
} from "@/slices/PreviousAddressSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  clearTwoFactorState,
  setChallenge2FA,
  setChallengeQuestion,
  setSessionId,
} from "@/slices/twoFactorAuthSlice";
import {
  clearCoApplicantState,
} from "@/slices/coApplicantSlice";
import FadeTransition from "@/styles/animation/FadeTransition";
import { useAuth } from "@/context/AuthContext";
import { setEmploymentStatus, setEmploymentType, setEmploymentIncome, setPayrollFrequency, setPayrollType, clearEmploymentState, setEmploymentId, setJobTitle, setOccupation, setIndustryYears, setIndustryMonths, setEmploymentYears, setEmploymentMonths, setEmployerName, setEmployerPhone, setEmployerAddress, setFamilyOwned, setOnLeave, setProbationExists, setBusinessLendingExists } from "@/slices/employmentSlice";
import Cookies from "js-cookie";
import { processUserAppData, setSingleClickInsightEvent } from "@/lib/util";
import { motion } from "framer-motion";
import { clearPreviousEmploymentState } from "@/slices/previousEmploymentSlice";
import { clearCurrentEmploymentState } from "@/slices/currentEmploymentSlice";
import { clearSessionState } from "@/slices/sessionDataSlice";
import { setReferralProducts } from "@/slices/referralProductSlice";
import { addAsset, assetInfo, clearAssetState } from "@/slices/assetSlice";
import { clearCurrentSession } from "@/slices/sessionDataSlice";
import { clearExpenseState } from "@/slices/expenseSlice";

export default function Login() {
  const client = createApiClient({ timeout: 60000 });
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useAuth();
  const { state } = useLocation();

  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [displayAttemptsExceeded, setDisplayAttemptsExceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Sorry, we have locked your account because there have been too many attempts. Please call us at 1-866-592-2226 to unlock your account."
  );
  const [loginInProgress, setLoginInProgress] = useState(false);

  const application = useSelector(
    (state: RootState) => state.application.application
  );

  const methods = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    document.title = "Meridian Lending - Sign In";
    setSingleClickInsightEvent('login');
    if (application.applicationErrorCode != "") {
      setErrorMessage(application.applicationErrorMessage);
      setDisplayError(true);
    }
    if (state && state.passwordChanged) {
      setPasswordReset(true);
    } else if (state && state.accountLocked) {
      setErrorMessage("Sorry, we have locked your account because there have been too many failed security code attempts. Please call us at 1-866-592-2226 to unlock your account.");
      setDisplayError(true);
    } else if (state && state.invalidMember) {
      setErrorMessage("Please enter your individual Member number, not the joint Member number, to start your application.");
      setDisplayError(true);
    }
    dispatch(clearSessionState());
    dispatch(clearCredentialState());
    dispatch(clearEmploymentState());
    dispatch(clearAssetState());
    dispatch(clearExpenseState());
    dispatch(clearAddressState());
    dispatch(clearMailingAddressState());
    dispatch(clearPreviousAddressState());
    dispatch(clearCurrentEmploymentState());
    dispatch(clearPreviousEmploymentState());
    dispatch(clearProgressState());
    dispatch(clearLegalTermsState());
    dispatch(clearJointState());
    dispatch(clearCoApplicantState());
    dispatch(clearPropertyInfoState());
    dispatch(clearPropertyAddressState());
    dispatch(clearTwoFactorState());
    dispatch(clearVerificationCounters());
    dispatch(clearCurrentSession());
  }, []);

  const handleClick = (type: number) => {
    //Validate login
    if (type == 1) {
      setDisplayError(false);
      setDisplayAttemptsExceeded(false);
      setLoginInProgress(true);
      let missingFields = false;

      //Ensure fields are not empty
      if (methods.getValues("email").trim() == "") {
        methods.setError("email", {
          type: "input",
          message: "Email or Member Number is required.",
        });
        missingFields = true;
      }
      if (methods.getValues("password").trim() == "") {
        methods.setError("password", {
          type: "input",
          message: "Password is required.",
        });
        missingFields = true;
      }
      if (methods.formState.errors.email || methods.formState.errors.password || missingFields) {
        setLoginInProgress(false);
        return;
      }

      //Check if a token is stored as a cookie
      let savedToken = Cookies.get(methods.getValues("email"));

      //Check if credentials is a member number; if not, use prospect login endpoint
      client
        .apiAuthLogin(
          new UserDto({
            email: methods.getValues("email"),
            password: methods.getValues("password"),
            clientToken: savedToken ? savedToken : undefined
          })
        )
        .then(function (loginResult) {
          setLoginInProgress(false);

          if (loginResult.applicant != undefined) {
            dispatch(setApplicantId(loginResult.applicant.id ? loginResult.applicant.id : 0));
            dispatch(setIsProspect(loginResult.applicant.memberNo == undefined || loginResult.applicant.memberNo == 0));
            dispatch(setFmApplicationId(loginResult.applicant.fmApplicationId ? loginResult.applicant.fmApplicationId : ""));
            dispatch(setFmApplicantId(loginResult.applicant.fmApplicantId ? loginResult.applicant.fmApplicantId : ""));
          }

          //Prospect Login
          if (loginResult.accessToken != undefined && loginResult.auth2FA == undefined) {
            let authenticatedClient = createApiClient({
              timeout: 60000,
              token: loginResult.accessToken,
            });
            authenticatedClient
              .apiApplicationsFetch()
              .then((result) => {
                if (result.applicant != undefined) {
                  let applicantInfo = result.applicant;
                  dispatch(setPhoneNumber(applicantInfo == undefined ? "" : applicantInfo.phoneNumber));
                  dispatch(setTitle(applicantInfo == undefined ? "" : applicantInfo.title));
                  dispatch(setFirstName(applicantInfo == undefined ? "" : applicantInfo.firstName));
                  dispatch(setMiddleName(applicantInfo == undefined ? "" : applicantInfo.middleName));
                  dispatch(setLastName(applicantInfo == undefined ? "" : applicantInfo.lastName));
                  dispatch(setDateOfBirthYear(applicantInfo == undefined ? "" : applicantInfo.dob_year));
                  dispatch(setDateOfBirthMonth(applicantInfo == undefined ? "" : applicantInfo.dob_month));
                  dispatch(setDateOfBirthDay(applicantInfo == undefined ? "" : applicantInfo.dob_day));
                  dispatch(setSin(applicantInfo == undefined ? "" : applicantInfo.sin));
                  dispatch(setPhoneType(applicantInfo == undefined ? "" : applicantInfo.phoneType));
                  dispatch(setMaritalStatus(applicantInfo == undefined ? "" : applicantInfo.maritalStatus));
                  dispatch(setNumOfDependents(applicantInfo == undefined ? "" : applicantInfo.numOfDependents));
                  dispatch(setCitizenshipStatus(applicantInfo == undefined ? "" : applicantInfo.citizenshipStatus));
                  dispatch(setFirstTimeBuyer(applicantInfo == undefined ? false : applicantInfo.firstTimeBuyer));
                  dispatch(setRecentResident(applicantInfo == undefined ? false : applicantInfo.recentResident));

                  dispatch(setTwilioResult(applicantInfo.twilioResult));
                  dispatch(setIdVerificationStatus(applicantInfo == undefined ? "" : applicantInfo.idVerificationStatus))
                  dispatch(setAlloySessionId(applicantInfo == undefined ? "" : applicantInfo.alloySessionId))
                  dispatch(setIdvId(applicantInfo == undefined ? 0 : applicantInfo.idvId))
                }

                if (result.employment != undefined) {
                  let employmentInfo = result.employment;
                  dispatch(setEmploymentId(employmentInfo == undefined ? "" : employmentInfo.id));
                  dispatch(setEmploymentStatus(employmentInfo == undefined ? "" : employmentInfo.status));
                  dispatch(setEmploymentType(employmentInfo == undefined ? "" : employmentInfo.type));

                  dispatch(setOccupation(employmentInfo == undefined ? "" : employmentInfo.occupation));
                  dispatch(setJobTitle(employmentInfo == undefined ? "" : employmentInfo.jobTitle));
                  dispatch(setIndustryYears(employmentInfo == undefined ? 0 : employmentInfo.industryYears));
                  dispatch(setIndustryMonths(employmentInfo == undefined ? 0 : employmentInfo.industryMonths));
                  dispatch(setEmploymentYears(employmentInfo == undefined ? 0 : employmentInfo.employmentYears));
                  dispatch(setEmploymentMonths(employmentInfo == undefined ? 0 : employmentInfo.employmentMonths));
                  dispatch(setBusinessLendingExists(employmentInfo.hasBusinessLending));

                  dispatch(setEmployerName(employmentInfo == undefined ? "" : employmentInfo.employerName));
                  dispatch(setEmployerPhone(employmentInfo == undefined ? "" : employmentInfo.employerPhone));
                  dispatch(setEmployerAddress(employmentInfo == undefined ? 0 : employmentInfo.employerAddress));

                  dispatch(setProbationExists(employmentInfo.isProbation));
                  dispatch(setFamilyOwned(employmentInfo.isFamilyOwned));
                  dispatch(setOnLeave(employmentInfo.isOnLeave));

                  dispatch(setPayrollType(employmentInfo == undefined ? "" : employmentInfo.payrollType));
                  dispatch(setEmploymentIncome(employmentInfo == undefined ? 0 : +employmentInfo.income));
                  dispatch(setPayrollFrequency(employmentInfo == undefined ? "" : employmentInfo.payrollFrequency));
                }

                if (result.application != undefined) {
                  let applicationInfo = result.application;
                  if (applicationInfo.applicationCategory != undefined && applicationInfo.applicationCategory != 0) {
                    dispatch(setApplicationCategory(applicationInfo.applicationCategory === 1 ? applicationCategory.Buy
                      : applicationInfo.applicationCategory === 2 ? applicationCategory.Refinance : applicationCategory.Transfer));
                  }
                  dispatch(setSearchingForProperty(applicationInfo.searchingForProperty));
                  dispatch(setPurchaseTypeSelected(applicationInfo.purchaseTypeSelected));
                  if (applicationInfo.id == 0) {
                    dispatch(setTermLength(applicationInfo.termLength));
                    dispatch(setAmortizationLength(applicationInfo.amortizationLength));
                    dispatch(setDownPayment(applicationInfo.downPayment));
                    dispatch(setTotalLoan(applicationInfo.totalLoan));
                  }

                  dispatch(setApplicationProgress(applicationInfo.applicationProgress));
                  dispatch(setLegalComplete(applicationInfo.applicationProgress >= 16));
                  dispatch(
                    setWelcomeEmailSent(applicationInfo.welcomeEmailSent)
                  );
                  dispatch(setApplicationEndpointReached(applicationInfo.applicationEndpointReached));

                  dispatch(setAlloyApplicationToken(applicationInfo.alloyApplicationToken));
                  dispatch(setIdVerificationSubmitted(applicationInfo.idVerificationSubmitted));
                  dispatch(setWelcomeEmailSent(applicationInfo.welcomeEmailSent));
                  dispatch(setUnderStaffReview(applicationInfo.underStaffReview));
                  if (applicationInfo.jointCode != undefined && applicationInfo.jointCode != "") {
                    dispatch(setJointCode(applicationInfo.jointCode));
                  }
                  if (applicationInfo.jointRelationship != undefined && applicationInfo.jointRelationship != "") {
                    dispatch(setJointRelationship(applicationInfo.jointRelationship));
                  }
                  if (applicationInfo.jointPrimaryApplicant != undefined && applicationInfo.jointPrimaryApplicant != "") {
                    dispatch(setJointPrimaryApplicant(applicationInfo.jointPrimaryApplicant));
                  }
                  dispatch(setJointAccepted(applicationInfo.jointAccepted));
                }

                if (result.propertyInfo != undefined) {
                  let propertyInfo = result.propertyInfo;
                  dispatch(setPropertyValue(propertyInfo.propertyValue));
                  if (result.application != undefined && result.application.id == 0) {
                    dispatch(setPurchasePrice(propertyInfo.purchasePrice));
                    dispatch(setOriginalPurchasePrice(propertyInfo.originalPurchasePrice));
                    dispatch(setMortgageBalance(propertyInfo.mortgageBalance));
                  }

                  dispatch(setPurposeOfProperty(propertyInfo.purposeOfProperty));
                  dispatch(setRentOut(propertyInfo.rentOut));
                  dispatch(setSeparateUnit(propertyInfo.separateUnit));
                  dispatch(setOwnOtherRentalProperties(propertyInfo.ownOtherRentalProperties));
                  dispatch(setNumOfRentalProperties(propertyInfo.numOfRentalProperties));
                  dispatch(setNumOfRentalUnits(propertyInfo.numOfRentalUnits));
                  dispatch(setClosingDate(propertyInfo.closingDate));
                  dispatch(setFundingDate(propertyInfo.fundingDate));
                  dispatch(setFinancingConditionExists(propertyInfo.financingConditionExists));
                  dispatch(setFinancingConditionDate(propertyInfo.financingConditionDate));
                }

                if (result.legalTerms != undefined) {
                  let amlTerms = result.legalTerms;
                  dispatch(setResStatus(amlTerms.resStatus));
                  dispatch(setTaxResidentorCitizenUs(amlTerms.taxResidentorCitizenUs));
                  dispatch(setTaxNotCanadaNotUS(amlTerms.taxNotCanadaNotUS));
                  dispatch(setPolExposed(amlTerms.polExposed));
                  dispatch(setOntarioWork(amlTerms.ontarioWork));
                  dispatch(setOwnerOperator(amlTerms.ownerOperator));
                  dispatch(setMeridianOnly(amlTerms.meridianOnly));
                  //dispatch(setOccupation(amlTerms.occupation));
                  dispatch(setNatureOfBanking(amlTerms.natureOfBanking));
                  dispatch(setPrimaryPurpose(amlTerms.primaryPurpose));
                  dispatch(setPrimaryPurposeGIC(amlTerms.primaryPurposeGIC));
                  dispatch(setPrimaryOtherPurposeGIC(amlTerms.primaryOtherPurposeGIC));
                  dispatch(setAMLFieldsCompleted(amlTerms.fieldsCompleted));
                }

                if (result.referralProducts != undefined && (referralProducts == undefined || referralProducts.length == 0)) {
                  dispatch(setReferralProducts(result.referralProducts));
                }else{
                  if (result.application != undefined){
                    let applicationInfo = result.application;
                    dispatch(setIsCoApplicant(applicationInfo.isCoApplicant));
                  }
                }
              })
              .catch((error) => {
                console.log("Error saving application info", error);
              });

            dispatch(setEmail(methods.getValues("email")));
            dispatch(setCredentialsCreated(true));
            dispatch(setCustNo(loginResult.custNo != undefined ? loginResult.custNo : 0));
            login(
              loginResult.accessToken != undefined ? loginResult.accessToken : "",
              loginResult.refreshToken != undefined ? loginResult.refreshToken : ""
            );

            navigate("/home", { replace: true });
          }
          //Existing Member (with Prompt for 2FA or Security Question)
          else if (
            loginResult.accessToken == undefined &&
            loginResult.auth2FA != undefined
          ) {
            dispatch(setEmail(methods.getValues("email")));
            dispatch(setIsProspect(false));
            dispatch(setAccessToken(loginResult.accessToken != undefined ? loginResult.accessToken : ""));
            dispatch(setChallenge2FA(loginResult.auth2FA?.challenge2FA));
            dispatch(setChallengeQuestion(loginResult.auth2FA?.challengeQuestion));
            dispatch(setSessionId(loginResult.auth2FA?.sessionId));

            if (
              loginResult.auth2FA != undefined &&
              loginResult.auth2FA.challengeQuestion != undefined
            ) {
              navigate("/security");
            } else if (
              loginResult.auth2FA != undefined &&
              loginResult.auth2FA.challenge2FA != undefined
            ) {
              navigate("/two-fa-security");
            }
          }
          //Existing Member (with 'Remember Me' Option Selected from Previous Session)
          else if (loginResult.accessToken != undefined && loginResult.auth2FA != undefined) {
            dispatch(setCredentialsCreated(true));
            login(loginResult.accessToken != undefined ? loginResult.accessToken : "", loginResult.refreshToken != undefined ? loginResult.refreshToken : "");
            if (loginResult.applicant != undefined) {
              let applicantInfo = loginResult.applicant;

              let dobMonth = applicantInfo.dateOfBirth.month() + 1;
              let dobDay = applicantInfo.dateOfBirth.date();

              dispatch(setApplicantId(applicantInfo.id ? applicantInfo.id : 0));
              dispatch(setTitle(applicantInfo.title ? applicantInfo.title : ""));
              dispatch(setFirstName(applicantInfo.firstName));
              dispatch(setMiddleName(applicantInfo.middleName ? applicantInfo.middleName : ""));
              dispatch(setLastName(applicantInfo.lastName));
              dispatch(setPhoneNumber(applicantInfo.phoneNumber));
              dispatch(setPhoneType(applicantInfo.phoneType ? applicantInfo.phoneType.replace(/\w/, p => p.toUpperCase()) : ""));
              dispatch(setEmailAddress(applicantInfo.email));
              dispatch(setDateOfBirthYear(applicantInfo.dateOfBirth.year() + ""));
              dispatch(setDateOfBirthMonth(dobMonth.toString().length == 1 ?
                "0" + dobMonth.toString() : dobMonth.toString()));
              dispatch(setDateOfBirthDay(dobDay + ""));
              dispatch(setMaritalStatus(applicantInfo.maritalStatus ? applicantInfo.maritalStatus : ""));
              dispatch(setNumOfDependents(applicantInfo.numberOfDependents ? applicantInfo.numberOfDependents : 0));

              if (applicantInfo.address) {
                dispatch(setAddressId(applicantInfo.address[0].id ? applicantInfo.address[0].id : 0));
                dispatch(setStreetNumber(applicantInfo.address[0].streetNumber ? applicantInfo.address[0].streetNumber : ""));
                dispatch(setStreetName(applicantInfo.address[0].streetName ? applicantInfo.address[0].streetName : ""));
                dispatch(setStreetType(applicantInfo.address[0].streetType ? applicantInfo.address[0].streetType : ""));
                dispatch(setUnitType(applicantInfo.address[0].unitType ? applicantInfo.address[0].unitType : ""));
                dispatch(setUnitNumber(applicantInfo.address[0].unitNumber ? applicantInfo.address[0].unitNumber : ""));
                dispatch(setCity(applicantInfo.address[0].city ? applicantInfo.address[0].city : ""));
                dispatch(setProvince(applicantInfo.address[0].region ? applicantInfo.address[0].region : ""));
                dispatch(setPostalCode(applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode : ""));
                dispatch(setCountry(applicantInfo.address[0].country ? applicantInfo.address[0].country : ""));

                //Attempt to parse the street type based on whether it contains multiple words
                if ((applicantInfo.address[0].streetType == undefined || applicantInfo.address[0].streetType == "") && applicantInfo.address[0].streetName && applicantInfo.address[0].streetName.split(" ").length > 1) {
                  let streetNameComponents = applicantInfo.address[0].streetName.split(" ");
                  let newStreetName = "";
                  for (let i = 0; i < streetNameComponents.length - 1; i++) {
                    newStreetName += streetNameComponents[i] + " ";
                  }
                  let streetType = streetNameComponents[streetNameComponents.length - 1].toUpperCase();
                  dispatch(setStreetType(streetType));
                  dispatch(setStreetName(newStreetName.trim()));
                }else if (applicantInfo.address[0].streetType != undefined && applicantInfo.address[0].streetType != "") {
                  dispatch(setStreetType(applicantInfo.address[0].streetType));
                }
              }

              //Check Validity of Ovation Fields
              let phoneNumberReg = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/);
              dispatch(setExistingMemberFields({
                isFirstNameAvailable: applicantInfo.firstName.trim() != '',
                isLastNameAvailable: applicantInfo.lastName.trim() != '',
                isPhoneNumberAvailable: phoneNumberReg.test(applicantInfo.phoneNumber),
                isSinAvailable: true,
                isProvinceAvailable: applicantInfo.address != undefined && applicantInfo.address[0].region ? applicantInfo.address[0].region.trim() != '' : false,
                isPostalCodeAvailable: applicantInfo.address != undefined && applicantInfo.address[0].postalCode ? applicantInfo.address[0].postalCode.trim() != '' : false,
                isCountryAvailable: applicantInfo.address != undefined && applicantInfo.address[0].country.trim() != ''
              }));
            }

            let authenticatedClient = createApiClient({ timeout: 60000, token: loginResult.accessToken });
            authenticatedClient.apiApplicationsFetch().then(async (appData) => {
              if (appData) {

                //Prefil member info such as subject property, address, etc.
                processUserAppData(appData, dispatch, false);

                //TODO: If member attempts to sign in with a different invitiation code; redirect to login screen and display error message
                navigate("/home", { replace: true });

              } else {
                navigate("/confirm-consent", { replace: true });
              }
            }).catch((error) => {
              console.log("Error fetching application info", error);
            });
          }
        })
        .catch((error) => {
          if (error && error.response) {
            let errorMessage = "";
            if (error.response && error.response.code == "LB10046001") {
              errorMessage = "Sorry, we are having trouble signing you in. You may have entered an incorrect Member Number and/or Password. "
                + "To prevent being locked out, select continue to sign in through online banking or reset your password";
              setDisplayAttemptsExceeded(true);
            } else if (error.response && error.response.message) {
              errorMessage = error.response.message;
              setDisplayError(true);
            } else {
              errorMessage = "An unexpected error occurred; please try again later.";
              setDisplayError(true);
            }
            setErrorMessage(errorMessage);
          }
          setLoginInProgress(false);
          if (methods.formState.errors.email || methods.formState.errors.password) {
            return;
          }
        });
      return;
    } else {
      navigate("/forgot-password");
    }
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  };

  return (
    <FadeTransition>
      <main aria-labelledby="main-content">
        <div className="main-container max-md:mt-10">
          <div className={clsx(style.login, "container", "mt-10")}>
            <div className={style.emHeading}>
              <div className="grid grid-cols-1">
                <h1>Sign in to your account</h1>
                <p>If you are currently a Meridian Member,</p>
                <p>sign in using your Member number.</p>
              </div>
            </div>
            {passwordReset && (
              <div
                className={clsx(
                  "flex flex-row pcaautocomplete mt-5",
                  style.successContainer
                )}
              >
                <img src="assets/success-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">
                  Your password was successfully updated. Please sign in to
                  continue.
                </span>
              </div>
            )}
            {displayError && (
              <div
                className={clsx(
                  "flex flex-row pcaautocomplete mt-5",
                  style.errorContainer
                )}
              >
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">{errorMessage}</span>
              </div>
            )}
            {displayAttemptsExceeded && (
              <div
                className={clsx(
                  "flex flex-row pcaautocomplete mt-5",
                  style.errorContainer
                )}
              >
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">Sorry, we are having trouble signing you in. You may have entered an incorrect Member Number and/or Password.
                  To prevent being locked out, select <a className="brand-blue-text cursor-pointer" href="https://banking.meridiancu.ca/Security_Login"
                    style={{ textDecorationLine: "underline" }}>continue</a> to sign in through online banking or reset your password.</span>
              </div>
            )}
            <div className="mt-5">
              <label htmlFor="email">Email Address or Member number</label>
              <div className="flex relative">
                <span>
                  <img
                    src="assets/error-icon.svg"
                    className={clsx(
                      methods.formState?.errors?.email ? "" : "hidden",
                      "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                    )}
                    alt="error"
                    height={5}
                    width={5}
                  />
                </span>
                <input
                  className={clsx(
                    methods.formState?.errors?.email?.message
                      ? "error"
                      : "",
                    "mt-2"
                  )}
                  type="email"
                  id="email"
                  autoComplete="email"
                  {...methods.register("email", {
                    required: "Enter your email address",
                  })}
                  onChange={(e) => {
                    if (e.target.value.trim() != "") {
                      methods.setValue("email", e.target.value);
                      methods.clearErrors("email");
                    }
                  }}
                />
              </div>
            </div>
            {methods.formState?.errors?.email?.message && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {methods.formState.errors.email.message}
              </span>
            )}
            <div className="mt-3 pass-wrapper">
              <label htmlFor="password">Password</label>
              <div className="flex relative">
                <span className={clsx(passwordShown ? "hidden" : " ")}>
                  <img
                    onClick={togglePasswordVisiblity}
                    src="assets/eye-icon.svg"
                    className={clsx(
                      "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                    )}
                    alt="error"
                    height={5}
                    width={5}
                  />
                </span>
                <span className={clsx(passwordShown ? " " : "hidden")}>
                  <img
                    onClick={togglePasswordVisiblity}
                    src="assets/eye-slash-icon.svg"
                    className={clsx(
                      "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                    )}
                    alt="error"
                    height={5}
                    width={5}
                  />
                </span>
                <input
                  className={clsx(
                    methods.formState?.errors?.password?.message
                      ? "error"
                      : "",
                    "mt-2"
                  )}
                  type={passwordShown ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  {...methods.register("password", {
                    required: "Enter your password",
                  })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleClick(1);
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value.trim() != "") {
                      methods.setValue("password", e.target.value);
                      methods.clearErrors("password");
                    }
                  }}
                />
              </div>
            </div>
            {methods.formState?.errors?.password?.message && (
              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                {methods.formState.errors.password.message}
              </span>
            )}
            <div className="mt-3">
              <a onClick={() => { handleClick(2) }}>
                <p className="active underline cursor-pointer">Forgot your password?</p>
              </a>
            </div>
            <div className="flex relative">
              <button
                onClick={() => {
                  handleClick(1);
                }}
                disabled={loginInProgress}
                className={clsx(
                  "primary mt-5 w-full sm:w-28 h-11 px-6",
                  loginInProgress ? style.loginDisabled : ""
                )}
              >
                {loginInProgress ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      style={{ marginRight: 8 }}
                      className={clsx(loginInProgress ? "" : "hidden")}
                    />
                  </motion.div>
                ) : null}
                Sign In
              </button>
            </div>
            <div className="text-center mt-5">
              <p className="mt-5 mb-3">
                If you're having difficulty signing in, please call
              </p>
              <p className="active">1-866-592-2226</p>
            </div>
          </div>
          <div className="text-center">
            <p className="mt-3 mb-3">
              This site is protected by reCAPTCHA and the{" "}
            </p>
            <p className="">
              Google{" "}
              <a
                className="active"
                href="https://policies.google.com/privacy"
                target="_blank"
              >
                Privacy Statement
              </a>{" "}
              and{" "}
              <a
                className="active"
                href="https://policies.google.com/terms"
                target="_blank"
              >
                Terms of Service
              </a>{" "}
              apply.
            </p>
          </div>
        </div>
      </main>
    </FadeTransition>
  );
}
